import { useCallback, useEffect, useState } from 'react';
import { DirectoryPicker } from '../components/DirectoryPicker';
import { LineSetup } from '../components/LineSetup';
import { TeamsPanel, accountType, typeLabel, type InventoryRow } from './TeamsPanel';
import { useApi } from '../context';
import type { Line, Team } from '../types';

/**
 * Admin: configure the lines the bot answers. Each line is set up in one place (bot & number,
 * recording notice, call flow) via <LineSetup>. Adding a line offers an existing bot account
 * or provisions a new one inline.
 */
export function AdminPage() {
  const api = useApi();
  const [lines, setLines] = useState<Line[]>([]);
  const [inventory, setInventory] = useState<InventoryRow[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [addMode, setAddMode] = useState<'existing' | 'new'>('existing');
  const [draft, setDraft] = useState({ name: '', resourceAccountOid: '', number: '' });

  const load = useCallback(() => {
    api.get<Line[]>('/api/admin/lines').then(setLines, (e: Error) => setError(e.message));
    api.get<InventoryRow[]>('/api/admin/teams/inventory').then(setInventory, () => undefined);
    api.get<Team[]>('/api/teams').then(setTeams, () => undefined);
  }, [api]);
  useEffect(load, [load]);

  const botAccounts = inventory.filter((r) => r.kind === 'resource_account' && accountType(r) === 'bot');
  const numbers = inventory.filter((r) => r.kind === 'phone_number');

  function addLine() {
    api.post('/api/admin/lines', {
      resourceAccountOid: draft.resourceAccountOid.trim(),
      number: draft.number.trim() || null,
      name: draft.name.trim(),
      ringTargets: '',
    }).then(() => {
      setDraft({ name: '', resourceAccountOid: '', number: '' });
      load();
    }, (e: Error) => setError(e.message));
  }

  return (
    <>
      <h1>Configuration</h1>
      {error && <p className="error">{error}</p>}

      <TeamsSection teams={teams} onChanged={load} onError={setError} />

      <h2>Lines</h2>
      <p className="hint">Each line is one number the bot answers. Open <em>set up</em> to choose its bot account &amp; number, add the “calls may be recorded” message, and design the call flow.</p>
      {lines.map((l) => <LineSetup key={l.id} line={l} inventory={inventory} teams={teams} onChanged={load} />)}
      {lines.length === 0 && <p className="sub">No lines yet — add one below.</p>}

      <div className="line-card">
        <strong>Add a line</strong>
        <div className="filters" style={{ marginTop: '0.5rem' }}>
          <label className="sub"><input type="radio" name="addmode" checked={addMode === 'existing'} onChange={() => setAddMode('existing')} /> Use an existing bot account</label>
          <label className="sub"><input type="radio" name="addmode" checked={addMode === 'new'} onChange={() => setAddMode('new')} /> Provision a new bot</label>
        </div>
        {addMode === 'existing' ? (
          <>
            <div className="filters">
              <input placeholder="Name (e.g. HLHC On-Call London)" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              {numbers.length > 0 ? (
                <select value={draft.number} onChange={(e) => setDraft({ ...draft, number: e.target.value })}>
                  <option value="">— number (optional) —</option>
                  {numbers.map((n) => <option key={n.key} value={n.key}>{n.key} — {String(n.data['assignedName'] ?? '')} [{typeLabel[String(n.data['holderType'])] ?? 'Other'}]</option>)}
                </select>
              ) : (
                <input placeholder="+44… (optional)" value={draft.number} onChange={(e) => setDraft({ ...draft, number: e.target.value })} style={{ width: '9rem' }} />
              )}
              <select value={draft.resourceAccountOid} onChange={(e) => setDraft({ ...draft, resourceAccountOid: e.target.value })} style={{ minWidth: '16rem' }}>
                <option value="">— bot resource account —</option>
                {botAccounts.map((a) => <option key={a.key} value={a.key}>{a.name} ({String(a.data['upn'] ?? '')})</option>)}
              </select>
              <DirectoryPicker label="🔍 find bot account" onPick={(e) => setDraft((d) => ({ ...d, resourceAccountOid: e.id, name: d.name || e.displayName }))} />
              <button className="primary" onClick={addLine} disabled={!draft.name.trim() || !draft.resourceAccountOid.trim()}>Add line</button>
            </div>
            <p className="hint">Then open <em>set up</em> on the new line to add its recording notice and call flow.</p>
          </>
        ) : (
          <TeamsPanel lines={lines} onLinesChanged={load} />
        )}
      </div>
    </>
  );
}

function TeamsSection({ teams, onChanged, onError }: { teams: Team[]; onChanged: () => void; onError: (m: string) => void }) {
  const api = useApi();
  const [name, setName] = useState('');
  const run = (p: Promise<unknown>) => p.then(onChanged).catch((e: Error) => onError(e.message));
  return (
    <>
      <h2>Teams</h2>
      <p className="hint">Teams scope who sees a call: assign users to teams (Users &amp; Access) and map each line to a team below. A line with no team is visible to everyone.</p>
      <div className="line-card">
        {teams.map((t) => <TeamRow key={t.id} team={t} run={run} />)}
        {teams.length === 0 && <p className="sub">No teams yet.</p>}
        <div className="filters">
          <input placeholder="New team name" value={name} onChange={(e) => setName(e.target.value)} />
          <button className="primary" disabled={!name.trim()} onClick={() => { run(api.post('/api/teams', { name: name.trim() })); setName(''); }}>Add team</button>
        </div>
      </div>
    </>
  );
}

function TeamRow({ team, run }: { team: Team; run: (p: Promise<unknown>) => void }) {
  const api = useApi();
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(team.name);
  return (
    <div className="filters">
      {edit ? (
        <>
          <input value={name} onChange={(e) => setName(e.target.value)} />
          <button className="primary" onClick={() => { run(api.patch(`/api/teams/${team.id}`, { name: name.trim() })); setEdit(false); }}>Save</button>
          <button className="link" onClick={() => { setName(team.name); setEdit(false); }}>cancel</button>
        </>
      ) : (
        <>
          <strong>{team.name}</strong>
          <span className="spacer" />
          <button className="link" onClick={() => setEdit(true)}>rename</button>
          <button className="link danger" onClick={() => { if (confirm(`Remove team "${team.name}"? Lines mapped to it become unteamed (visible to everyone) and user assignments are cleared.`)) run(api.delete(`/api/teams/${team.id}`)); }}>remove</button>
        </>
      )}
    </div>
  );
}
